import type { LockBadgeKind } from '../constants/statusRewardBadgePolicy';
import type { GmRewardPerk, GmStatusKey } from '../types/gm-statuses';
import type { GroupedPlayerRewardDto } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import type { ExtendedProgressStatusDto } from '@/queries/progress-statuses/progress-statuses.schema';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { statusKeys } from '../constants/statuses';
import { getBenefitLockBadges } from '../constants/statusRewardBadgePolicy';
import { STATUS_REWARD_BENEFITS } from '../constants/statusRewardData';
import { getStatusKey } from './getStatusKey';

export const FIRST_PLAYER_LEVEL = 1;

export type BenefitAvailability = 'available' | 'needs-status' | 'coming-soon' | 'unavailable';

export interface ResolvedStatusRewardBenefit {
  benefit: (typeof STATUS_REWARD_BENEFITS)[number];
  availability: BenefitAvailability;
  statusName?: string;
  groupedReward?: GroupedPlayerRewardDto;
  badges: ReturnType<typeof getBenefitLockBadges>;
}

type Benefit = (typeof STATUS_REWARD_BENEFITS)[number];
type AvailabilityResult = Pick<ResolvedStatusRewardBenefit, 'availability' | 'statusName'>;

interface Params {
  currentStatusKey?: GmStatusKey;
  groupedRewards?: GroupedPlayerRewardDto[];
  progressStatuses?: ExtendedProgressStatusDto[];
  playerLevel: number;
}

const resolveAvailability = (
  benefit: Benefit,
  groupedReward: GroupedPlayerRewardDto | undefined,
  { currentStatusKey, progressStatuses, playerLevel }: Params,
): AvailabilityResult => {
  // The frontend launch flag takes priority over any grouped reward.
  if (benefit.comingSoon) {
    return { availability: 'coming-soon' };
  }

  const isFirstLevelCard = benefit.type === GamificationRewardType.LevelUp && playerLevel === FIRST_PLAYER_LEVEL;
  const requiredLevel = groupedReward?.minLevelFrom;

  if (typeof requiredLevel === 'number') {
    return {
      availability: isFirstLevelCard || playerLevel >= requiredLevel ? 'available' : 'needs-status',
      statusName: progressStatuses?.find(status => status.levelFrom === requiredLevel)?.name,
    };
  }

  // Scratch cards have no status fallback when the API provides no level.
  if (benefit.type === GamificationRewardType.LevelUp || benefit.type === GamificationRewardType.StatusUp) {
    return { availability: isFirstLevelCard ? 'available' : 'unavailable' };
  }

  if (!benefit.unlocksWithStatus) {
    return { availability: 'unavailable' };
  }

  const requiredStatusKey = statusKeys.find(key => benefit.values[key]);
  if (!requiredStatusKey) {
    return { availability: 'unavailable' };
  }

  const isAvailable = currentStatusKey
    && statusKeys.indexOf(currentStatusKey) >= statusKeys.indexOf(requiredStatusKey);

  return {
    availability: isAvailable ? 'available' : 'needs-status',
    statusName: isAvailable
      ? undefined
      : progressStatuses?.find(status => getStatusKey(status) === requiredStatusKey)?.name,
  };
};

/** Builds the shared benefit state for the statuses view and rewards grid. */
export const buildStatusRewardBenefits = (params: Params): ResolvedStatusRewardBenefit[] => {
  return STATUS_REWARD_BENEFITS.map((benefit) => {
    const groupedReward = params.groupedRewards?.find(reward => reward.type === benefit.type);
    const { availability, statusName } = resolveAvailability(benefit, groupedReward, params);

    // A status fallback can lock a card, but only an API level can show Need status.
    const hasApiLevel = typeof groupedReward?.minLevelFrom === 'number';
    let badgeKind: LockBadgeKind | null = null;
    if (benefit.comingSoon) {
      badgeKind = 'coming-soon';
    } else if (hasApiLevel && availability === 'needs-status') {
      badgeKind = 'needs-status';
    }

    return {
      benefit,
      groupedReward,
      availability,
      statusName,
      badges: getBenefitLockBadges({ type: benefit.type, kind: badgeKind, statusName }),
    };
  });
};

/**
 * Used for building mobile status reward perks
 */
export const buildStatusRewardPerks = (benefits: ResolvedStatusRewardBenefit[], statusKey?: GmStatusKey) => {
  if (!statusKey) {
    return [];
  }

  return benefits.map(({ benefit, availability, badges }) => {
    const subTitle = benefit.values[statusKey] ?? '';
    const perkItem: GmRewardPerk = {
      id: benefit.translationKey,
      action: availability === 'available' ? benefit.action : undefined,
      link: availability === 'available' ? benefit.link : undefined,
      img: benefit.perkImg,
      type: benefit.type,
      title: benefit.title,
      subTitle,
      isDisabled: !subTitle,
    };

    return { perkItem, availability, lockBadge: badges.statuses, action: benefit.action };
  });
};
